// Reportes — DATOS REALES por cooperativa, calculados por el backend desde los libros
// contables: balance general, estado de resultados y excedentes anuales. Antes usaba datos
// de ejemplo.

import React, { useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import EncabezadoOperativo from '../components/EncabezadoOperativo';
import TablaDatos from '../components/TablaDatos';
import useCargar from '../hooks/useCargar';
import useCooperativaOperativa from '../hooks/useCooperativaOperativa';
import { contabilidadApi } from '../services/coopnetApi';
import { moneda } from '../utils/formato';

const Cargando = () => <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={28} /></Box>;

const Grupo = ({ titulo, filas, total }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" gutterBottom>{titulo}</Typography>
    <TablaDatos
      filas={[...filas, { codigo: '__total__', nombre: `Total ${titulo.toLowerCase()}`, saldo: total, total: true }]}
      claveFila={(f) => f.codigo}
      vacio="Sin saldos."
      columnas={[
        { titulo: 'Código', render: (f) => (f.total ? '' : f.codigo) },
        { titulo: 'Cuenta', render: (f) => (f.total ? <b>{f.nombre}</b> : f.nombre) },
        { titulo: 'Saldo', align: 'right', render: (f) => (f.total ? <b>{moneda(f.saldo)}</b> : moneda(f.saldo)) },
      ]}
    />
  </Box>
);

function Balance({ id }) {
  const { datos: d, cargando, error } = useCargar(() => contabilidadApi.balanceGeneral(id), [id]);
  if (cargando) return <Cargando />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!d) return null;
  const cuadra = Math.abs(d.totalActivo - (d.totalPasivo + d.totalPatrimonio)) < 0.5;
  return (
    <>
      <Box sx={{ mb: 2 }}><Chip color={cuadra ? 'success' : 'error'} label={cuadra ? 'Activo = Pasivo + Patrimonio' : 'El balance no cuadra'} /></Box>
      <Grupo titulo="Activo" filas={d.activo || []} total={d.totalActivo} />
      <Grupo titulo="Pasivo" filas={d.pasivo || []} total={d.totalPasivo} />
      <Grupo titulo="Patrimonio" filas={d.patrimonio || []} total={d.totalPatrimonio} />
    </>
  );
}

function Resultados({ id }) {
  const { datos: d, cargando, error } = useCargar(() => contabilidadApi.estadoResultados(id), [id]);
  if (cargando) return <Cargando />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!d) return null;
  const resultado = d.totalIngresos - d.totalGastos;
  return (
    <>
      <Grupo titulo="Ingresos" filas={d.ingresos || []} total={d.totalIngresos} />
      <Grupo titulo="Gastos" filas={d.gastos || []} total={d.totalGastos} />
      <Alert severity={resultado >= 0 ? 'success' : 'warning'}>
        {resultado >= 0 ? 'Excedente' : 'Déficit'} del período: <b>{moneda(Math.abs(resultado))}</b>
      </Alert>
    </>
  );
}

function Excedentes({ id }) {
  const { datos, cargando, error } = useCargar(() => contabilidadApi.excedentesAnuales(id), [id], []);
  if (cargando) return <Cargando />;
  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TablaDatos
        filas={datos}
        claveFila={(x) => x.anio}
        vacio="Aún no hay ejercicios cerrados. Los excedentes aparecen al cerrar el ejercicio en Contabilidad."
        columnas={[{ titulo: 'Año', render: (x) => x.anio }, { titulo: 'Excedente del año', align: 'right', render: (x) => moneda(x.excedenteDelAno) }]}
      />
    </>
  );
}

const TABS = [['Balance general', Balance], ['Estado de resultados', Resultados], ['Excedentes anuales', Excedentes]];

const Reportes = () => {
  const op = useCooperativaOperativa();
  const [tab, setTab] = useState(0);
  const Actual = TABS[tab][1];
  return (
    <EncabezadoOperativo
      titulo="Reportes"
      subtitulo="Reportes financieros"
      operativa={op}
      acciones={<Button startIcon={<PrintIcon />} onClick={() => window.print()}>Imprimir</Button>}
    >
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        {TABS.map(([t]) => <Tab key={t} label={t} />)}
      </Tabs>
      <Actual key={`${op.cooperativaId}-${tab}`} id={op.cooperativaId} />
    </EncabezadoOperativo>
  );
};

export default Reportes;
